// src/pages/NuevaMotoAdmin.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  collection,
  setDoc,
  getDoc,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";

import { uploadFotoJpg, uploadVideoAny } from "../services/storage";
import { webhookPublishAll } from "../services/webhooks";
import { generateCodigoMoto } from "../utils/codigoMoto";

import ph1 from "../assets/Foto_01.png";
import ph2 from "../assets/Foto_02.png";
import ph3 from "../assets/Foto_03.png";
import ph4 from "../assets/Foto_04.png";
import ph5 from "../assets/Foto_05.png";
import ph6 from "../assets/Foto_06.png";
import ph7 from "../assets/Foto_07.png";
import phVideo from "../assets/Video_01.png";

const FOTO_PLACEHOLDERS = [ph1, ph2, ph3, ph4, ph5, ph6, ph7];

export default function NuevaMotoAdmin() {
  const nav = useNavigate();

  // Perfil
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);

  // Listas para selector destino
  const [agencias, setAgencias] = useState([]);     // [{id, nombre, agenciaId, limitePublicaciones, usadas}]
  const [sucursales, setSucursales] = useState([]); // [{id, nombre}]
  const [destino, setDestino] = useState("");       // "sc:<id>" | "ag:<id>"

  // Documento creado (draft). Hasta que exista, no se puede subir media.
  const [motoId, setMotoId] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Formulario (usa sucursalId / sucursalNombre canónicos)
  const [form, setForm] = useState({
    nombre: "",
    codigoMotoPreview: "",
    marca: "",
    modelo: "",
    ano: "",
    color: "",
    kilometros: "",
    cilindrada: "",
    valor: "",
    valorRebajado: "",
    financiamiento: "No",
    origen: "Nacional",
    tipoMoto: "",
    categoria: "Seminueva",
    sucursalId: "",       // ← canónico
    sucursalNombre: "",   // ← canónico (visible en UI)
    descripcion: "",
    etiqueta: "",
    // asociaciones
    agenciaId: "",        // si destino es agencia
  });

  // Media
  const [main, setMain] = useState("");
  const [localFotos, setLocalFotos] = useState(Array(7).fill(null));
  const [videoUrl, setVideoUrl] = useState("");

  const fotoInputsRef = useRef(Array.from({ length: 7 }, () => null));
  const videoInputRef = useRef(null);

  // ===== Perfil admin =====
  useEffect(() => {
    (async () => {
      const u = auth.currentUser;
      if (!u) { nav("/"); return; }
      const usnap = await getDoc(doc(db, "users", u.uid));
      const data = usnap.exists() ? usnap.data() : null;
      setMe({ uid: u.uid, email: u.email, ...data });
      setLoadingMe(false);
    })();
  }, [nav]);

  // ===== Listas destino =====
  useEffect(() => {
    (async () => {
      if (!me || me.rol !== "admin") return;
      try {
        const agSnap = await getDocs(collection(db, "agencias"));
        const agList = agSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAgencias(agList);

        const scSnap = await getDocs(collection(db, "sucursales"));
        const scList = scSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSucursales(scList);

        // Prefijar destino por defecto
        if (scList.length > 0) {
          const s0 = scList[0];
          setDestino(`sc:${s0.id}`);
          setForm(v => ({
            ...v,
            sucursalId: s0.id,
            sucursalNombre: s0.nombre || s0.id,
            agenciaId: ""
          }));
        } else if (agList.length > 0) {
          const a0 = agList[0];
          setDestino(`ag:${a0.id}`);
          setForm(v => ({
            ...v,
            sucursalId: "", // agencias no usan sucursalId
            sucursalNombre: a0.nombre || a0.agenciaId || a0.id,
            agenciaId: a0.agenciaId || ""
          }));
        }
      } catch (e) {
        console.error("[NuevaMotoAdmin] cargar listas:", e);
      }
    })();
  }, [me]);

  // Cambiar destino
  const onChangeDestino = (val) => {
    setDestino(val);
    if (val.startsWith("sc:")) {
      const id = val.slice(3);
      const s = sucursales.find(x => x.id === id);
      setForm(v => ({
        ...v,
        sucursalId: s?.id || "",
        sucursalNombre: s?.nombre || s?.id || "",
        agenciaId: ""
      }));
    } else if (val.startsWith("ag:")) {
      const id = val.slice(3);
      const a = agencias.find(x => x.id === id);
      setForm(v => ({
        ...v,
        sucursalId: "", // no aplica
        sucursalNombre: a?.nombre || a?.agenciaId || a?.id || "",
        agenciaId: a?.agenciaId || ""
      }));
    }
  };

  // Código preview en vivo
  useEffect(() => {
    const codigo = generateCodigoMoto({ marca: form.marca, modelo: form.modelo, ano: form.ano });
    setForm(v => ({ ...v, codigoMotoPreview: codigo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.marca, form.modelo, form.ano]);

  const hasAllPhotos = useMemo(() => localFotos.every(Boolean), [localFotos]);
  const hasVideo = useMemo(() => !!videoUrl, [videoUrl]);
  const readyToPublish = hasAllPhotos && hasVideo;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(v => ({ ...v, [name]: value }));
  };

  // ===== Guardar datos (crear draft con auto-ID) =====
  const guardarDatos = async () => {
    if (!me || me.rol !== "admin") return;
    if (!destino) {
      alert("Selecciona una sucursal o agencia de destino.");
      return;
    }
    // Validaciones mínimas
    if (!form.marca || !form.modelo || !form.ano) {
      alert("Completa Marca, Modelo y Año.");
      return;
    }

    setSaving(true);
    try {
      const anoNum = form.ano ? Number(form.ano) : null;

      // Código definitivo con fecha
      const codigoMoto = generateCodigoMoto({
        marca: form.marca,
        modelo: form.modelo,
        ano: form.ano,
        date: new Date(),
      });

      const payload = {
        nombre: (form.nombre || "").trim(),
        codigoMoto,
        marca: (form.marca || "").trim(),
        modelo: (form.modelo || "").trim(),
        ano: anoNum,

        color: (form.color || "").trim(),
        kilometros: form.kilometros === "" ? null : Number(form.kilometros),
        cilindrada: form.cilindrada === "" ? null : Number(form.cilindrada),

        valor: form.valor === "" ? null : Number(form.valor),
        valorRebajado: form.valorRebajado === "" ? 0 : Number(form.valorRebajado),

        financiamiento: form.financiamiento === "Sí" ? "Sí" : "No",
        origen: form.origen === "Importada" ? "Importada" : "Nacional",
        tipoMoto: (form.tipoMoto || "").trim(),
        categoria: form.categoria === "Nueva" ? "Nueva" : "Seminueva",

        // 🔴 Canónicos de sucursal
        sucursalId: form.sucursalId || "",
        sucursalNombre: form.sucursalNombre || "",
        // 🔸 Legacy (compat visual temporal)
        sucursal: form.sucursalNombre || "",

        fuente: "Dashboard",
        descripcion: form.descripcion ?? "",
        estatus: "pending",
        etiqueta: form.etiqueta || "",

        // asociaciones
        ...(form.agenciaId ? { agenciaId: form.agenciaId } : {}),

        // media inicial vacía
        foto1: null, foto2: null, foto3: null, foto4: null, foto5: null, foto6: null, foto7: null,
        video: null,

        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, "motos", codigoMoto), payload, { merge: true });
      setMotoId(codigoMoto);
      alert("Datos guardados. Ya puedes subir fotos y video.");
    } catch (e) {
      console.error(e);
      alert("No se pudo guardar la ficha.");
    } finally {
      setSaving(false);
    }
  };

  // ===== Subidas (habilitadas SOLO si existe motoId) =====
  const canUpload = !!motoId;

  const uploadFoto = async (index, file) => {
    if (!canUpload) return;
    try {
      const { url } = await uploadFotoJpg(motoId, index + 1, file);
      setLocalFotos(prev => {
        const next = [...prev];
        next[index] = url;
        return next;
      });
      if (!main) setMain(url);

      const field = `foto${index + 1}`;
      await updateDoc(doc(db, "motos", motoId), {
        [field]: url,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error(err);
      alert("No se pudo subir la foto.");
    }
  };

  const uploadVideo = async (file) => {
    if (!canUpload) return;
    try {
      const url = await uploadVideoAny(motoId, file);
      setVideoUrl(url);
      await updateDoc(doc(db, "motos", motoId), {
        video: url,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error(err);
      alert("No se pudo subir el video.");
    }
  };

  // ===== Publicar ahora =====
  const publicarAhora = async () => {
    if (!motoId) {
      alert("Primero guarda la ficha y sube el media.");
      return;
    }
    if (!readyToPublish) {
      alert("Necesitas 7 fotos y 1 video para publicar.");
      return;
    }
    // 🔒 Si es SUCURSAL, exigir sucursalId antes de publicar
    if (destino.startsWith("sc:") && !form.sucursalId) {
      alert("Debes seleccionar una sucursal (ID) antes de publicar.");
      return;
    }

    setPublishing(true);
    try {
      // Payload coherente con canónicos (y legacy mirror)
      const payload = {
        nombre: (form.nombre || "").trim(),
        codigoMoto: form.codigoMotoPreview, // puede diferir del definitivo; ok para webhook
        marca: (form.marca || "").trim(),
        modelo: (form.modelo || "").trim(),
        ano: form.ano ? Number(form.ano) : null,

        color: (form.color || "").trim(),
        kilometros: form.kilometros === "" ? null : Number(form.kilometros),
        cilindrada: form.cilindrada === "" ? null : Number(form.cilindrada),

        valor: form.valor === "" ? null : Number(form.valor),
        valorRebajado: form.valorRebajado === "" ? 0 : Number(form.valorRebajado),

        financiamiento: form.financiamiento,
        origen: form.origen,
        tipoMoto: (form.tipoMoto || "").trim(),
        categoria: form.categoria,

        // 🔴 Canónicos de sucursal
        sucursalId: form.sucursalId || "",
        sucursalNombre: form.sucursalNombre || "",
        // 🔸 Legacy (compat visual temporal)
        sucursal: form.sucursalNombre || "",

        fuente: "Dashboard",
        descripcion: form.descripcion ?? "",
        estatus: "publicada",
        etiqueta: form.etiqueta || "",

        ...(form.agenciaId ? { agenciaId: form.agenciaId } : {}),

        foto1: localFotos[0], foto2: localFotos[1], foto3: localFotos[2],
        foto4: localFotos[3], foto5: localFotos[4], foto6: localFotos[5],
        foto7: localFotos[6],
        video: videoUrl,

        link_facebookPage: "",
        link_instagram: "",
        link_mercadoLibre: "",
        link_wooCommerce: "",
        link_youtube: "",
      };

      const fotosArr = localFotos.filter(Boolean);

      // Persistir publicación
      await updateDoc(doc(db, "motos", motoId), {
        ...payload,
        updatedAt: new Date().toISOString(),
        publishedOnce: true,
      });

      // Webhook de publicación (webhooks.js ya envía sucursalName desde sucursalNombre)
      await webhookPublishAll(motoId, payload, fotosArr, videoUrl);

      alert("Moto publicada.");
      nav(`/detalle/${motoId}`);
    } catch (e) {
      console.error(e);
      alert("No se pudo publicar.");
    } finally {
      setPublishing(false);
    }
  };

  if (loadingMe) return <div className="p-6 text-gray-600">Cargando…</div>;
  if (me?.rol !== "admin") return <div className="p-6 text-gray-600">Acceso restringido.</div>;

  const canUploadHint = "Guarda la ficha para habilitar esta acción.";

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Topbar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
        <button
          onClick={() => nav("/dashboard")}
          className="px-3 py-2 rounded bg-gray-900 text-white hover:bg-gray-800"
        >
          ← Volver
        </button>
        <h1 className="font-semibold truncate">Nueva Moto (Admin)</h1>
        <div />
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Galería (deshabilitada si no hay motoId) */}
        <div>
          {main ? (
            <img
              src={main}
              alt="principal"
              className="w-full h-96 object-cover rounded border border-gray-200 bg-white"
            />
          ) : (
            <div className="w-full h-96 rounded border border-gray-200 bg-gray-50" />
          )}

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
            {Array.from({ length: 7 }).map((_, i) => {
              const show = localFotos[i] || FOTO_PLACEHOLDERS[i];
              const disabled = !motoId;
              return (
                <div key={i} className="relative group">
                  <img
                    src={show}
                    alt={`foto-${i + 1}`}
                    className={`w-full aspect-square object-cover rounded border ${
                      main === (localFotos[i] || show) ? "border-gray-900" : "border-gray-200"
                    } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                    onClick={() => {
                      if (disabled) return;
                      (localFotos[i] ? setMain(localFotos[i]) : fotoInputsRef.current[i]?.click());
                    }}
                    title={disabled ? canUploadHint : ""}
                  />
                  <div className={`absolute inset-0 ${disabled ? "hidden" : "hidden group-hover:flex"} items-center justify-center gap-2 bg-black/30 rounded`}>
                    <button
                      className="px-2 py-1 text-xs bg-white text-gray-900 rounded border border-gray-200 shadow-sm"
                      onClick={() => fotoInputsRef.current[i]?.click()}
                    >
                      Subir
                    </button>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={(el) => (fotoInputsRef.current[i] = el)}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) await uploadFoto(i, file);
                    }}
                    disabled={disabled}
                  />
                </div>
              );
            })}
          </div>

          {/* Video */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Video</h3>
              <div className="flex gap-2">
                <button
                  className={`px-3 py-1 text-sm rounded ${motoId ? "bg-gray-900 text-white hover:bg-gray-800" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                  onClick={() => motoId && videoInputRef.current?.click()}
                  disabled={!motoId}
                  title={!motoId ? canUploadHint : ""}
                >
                  {videoUrl ? "Reemplazar video" : "Subir video"}
                </button>
              </div>
            </div>

            <div className="relative">
              {videoUrl ? (
                <video
                  src={videoUrl}
                  className="w-full h-52 rounded border border-gray-200 bg-white"
                  controls
                />
              ) : (
                <img
                  src={phVideo}
                  alt="video-ph"
                  className={`w-full h-52 object-cover rounded border border-gray-200 ${motoId ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
                  onClick={() => motoId && videoInputRef.current?.click()}
                  title={!motoId ? canUploadHint : ""}
                />
              )}
              <input
                type="file"
                accept="video/*"
                className="hidden"
                ref={videoInputRef}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) await uploadVideo(file);
                }}
                disabled={!motoId}
              />
            </div>
          </div>
        </div>

        {/* Formulario / Acciones */}
        <div className="space-y-4">
          {/* Selector de destino */}
          <div className="p-3 rounded border border-gray-200 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-600">Destino</label>
                <select
                  value={destino}
                  onChange={(e) => onChangeDestino(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"
                >
                  <optgroup label="Sucursales">
                    {sucursales.map(s => (
                      <option key={`sc:${s.id}`} value={`sc:${s.id}`}>
                        {s.nombre || s.id}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Agencias">
                    {agencias.map(a => (
                      <option key={`ag:${a.id}`} value={`ag:${a.id}`}>
                        {a.nombre || a.agenciaId || a.id}
                        {Number.isFinite(a.limitePublicaciones) ? ` — ${a.usadas || 0}/${a.limitePublicaciones}` : ""}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Sucursal/Agencia destino</label>
                <input
                  value={form.sucursalNombre}
                  readOnly
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-gray-100 text-gray-600"
                />
              </div>
            </div>
          </div>

          {/* Campos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600">Nombre</label>
              <input name="nombre" value={form.nombre} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Código (preview)</label>
              <input value={form.codigoMotoPreview} disabled className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-gray-100 text-gray-600"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Marca</label>
              <input name="marca" value={form.marca} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Modelo</label>
              <input name="modelo" value={form.modelo} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Año</label>
              <input name="ano" type="number" value={form.ano} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Color</label>
              <input name="color" value={form.color} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Kilómetros</label>
              <input name="kilometros" type="number" value={form.kilometros} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Cilindrada (cc)</label>
              <input name="cilindrada" type="number" value={form.cilindrada} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Precio (MXN)</label>
              <input name="valor" type="number" value={form.valor} onChange={onChange} placeholder="Ej. 125000" className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Precio Rebajado (MXN)</label>
              <input name="valorRebajado" type="number" value={form.valorRebajado} onChange={onChange} placeholder="0" className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Financiamiento</label>
              <select name="financiamiento" value={form.financiamiento} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900">
                <option value="Sí">Sí</option>
                <option value="No">No</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Origen</label>
              <select name="origen" value={form.origen} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900">
                <option value="Nacional">Nacional</option>
                <option value="Importada">Importada</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Tipo de moto</label>
              <input name="tipoMoto" value={form.tipoMoto} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"/>
            </div>

            <div>
              <label className="text-sm text-gray-600">Categoría</label>
              <select name="categoria" value={form.categoria} onChange={onChange} className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900">
                <option value="Seminueva">Seminueva</option>
                <option value="Nueva">Nueva</option>
              </select>
            </div>

            {/* Vista Solo-lectura del destino */}
            <div>
              <label className="text-sm text-gray-600">Sucursal / Agencia</label>
              <input value={form.sucursalNombre} readOnly className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-gray-100 text-gray-600"/>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-600">Descripción</label>
            <textarea
              name="descripcion"
              value={form.descripcion}
              onChange={onChange}
              className="mt-1 w-full h-28 px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="(una línea, sin saltos ni emojis)"
            />
          </div>

          {/* Acciones */}
          <div className="flex flex-wrap gap-3 items-center">
            <button
              onClick={guardarDatos}
              disabled={saving || !!motoId}
              className={`px-4 py-2 rounded ${motoId ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            >
              {motoId ? "Guardado" : (saving ? "Guardando…" : "Guardar datos")}
            </button>

            <button
              onClick={publicarAhora}
              disabled={!motoId || publishing || !readyToPublish}
              className={`px-4 py-2 rounded ${(!motoId || !readyToPublish) ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              title={!motoId ? "Primero guarda la ficha y sube media" : (!readyToPublish ? "Requiere 7 fotos y 1 video" : "")}
            >
              {publishing ? "Publicando…" : "Publicar ahora"}
            </button>

            {!motoId && <span className="text-sm text-gray-600">Guarda primero para habilitar subidas.</span>}
            {motoId && !readyToPublish && <span className="text-sm text-gray-600">Sube 7 fotos y 1 video para publicar.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}